<template>
	<AppLayout
		:title="trans('admin.audit.rotations.documentation.compromise_letters.title')"
		:return-url="route('association.representative-rotations.history.index')"
		:return-label="trans('admin.users.rotations.history')"
	>
		<Modal :id="modalId">
			<template #title>{{ trans('general.upload_file') }}</template>
			<LabeledObject full>
				<template #label>{{ trans('general.select_file') }}</template>
				<input type="file" accept="application/pdf" @change="loadFile($event)">
			</LabeledObject>
			<template #footer>
				<PrimaryButton :color="Color.Green" @click.stop="upload">
					<i class="fa-solid fa-upload"/>
				</PrimaryButton>
			</template>
		</Modal>
		<template v-if="canUpload" #actions>
			<a :href="route('association.representative-rotations.history.compromise-letters.show', candidate.id)">
				<SecondaryButton :color="Color.Blue">
					<i class="fa-solid fa-file-download mr-2"/>
					{{ trans('admin.audit.rotations.documentation.download') }}
				</SecondaryButton>
			</a>
			<PrimaryButton :color="Color.Green" @click.stop="showModal(modalId)">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.upload_file') }}
			</PrimaryButton>
		</template>
		<Empty v-if="letters.length === 0"/>
		<CardGrid v-else>
			<Card v-for="letter in letters" :key="`letter-${letter.id}`" :title="dayjs(letter.created_at).format(trans('general.dayjs.datetime'))">
				<template #badges>
					<Badge :color="letter.colors" active>{{ trans(`admin.audit.rotations.documentation.compromise_letters.status.${letter.status}`) }}</Badge>
					<Badge>#{{ letter.id }}</Badge>
				</template>
				<LabeledObject v-if="letter.is_rejected">
					<template #label>{{ trans('general.rejection_reason') }}</template>
					<p>{{ letter.rejection_reason }}</p>
				</LabeledObject>
			</Card>
		</CardGrid>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/Empty.vue'
import {defineProps, ref, type Ref} from 'vue'
import Card from '@/Components/Cards/Card.vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/Modal/Modal.vue'
import Badge from '@/Components/Badges/Badge.vue'
import CardGrid from '@/Components/Cards/CardGrid.vue'
import LabeledObject from '@/Components/LabeledObject.vue'
import {type Rotation} from '@/Types/Periods/Rotations/Rotation'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/Buttons/SecondaryButton.vue'
import {type RotationCandidate} from '@/Types/Periods/Rotations/RotationCandidate'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/helpers'
import {type CompromiseLetter} from '@/Types/Periods/Rotations/Documentation/CompromiseLetter'

const props = defineProps<{
	rotation:Rotation,
	candidate:RotationCandidate,
	letters:Array<CompromiseLetter>,
	canUpload:boolean,
}>()

const modalId:Ref<string> = ref('upload-compromise-letter-modal')
const letters:Ref<Array<CompromiseLetter>> = ref(props.letters)
const canUpload:Ref<boolean> = ref(props.canUpload)
const uploading:Ref<boolean> = ref(false)
const newLetter:Ref<CompromiseLetter> = ref({
	file: null,
} as CompromiseLetter)
const rotation:Ref<Rotation> = ref(props.rotation)
const candidate:Ref<RotationCandidate> = ref(props.candidate)

function loadFile(event):void{
	if(event.target && event.target.files){
		newLetter.value.file = event.target.files[0]
	}
}

function upload():void{
	if(!uploading.value){
		uploading.value = true
		const formData = new FormData
		formData.append('file', newLetter.value.file)
		formData.append('rotation_id', rotation.value.id)
		showInfo(trans('general.updating'))
		axios.post(route('association.representative-rotations.history.compromise-letters.store', candidate.value.id), formData, {
			headers: {
				'Content-Type': 'multipart/form-data',
			},
		}).then(({data}) => {
			showSuccess(trans('general.updated'))
			closeModal(modalId.value)
			canUpload.value = false
			letters.value.unshift(data)
		}).catch(showValidationErrors).finally(() => {
			uploading.value = false
		})
	}
}
</script>